import { flexRender, type Row } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import type * as React from 'react';
import { TableCell, TableRow } from '@avanta/shared/components/ui/Table';
import { cn } from '@avanta/shared/utils/cn';
import { type ExpandableTableProps } from './types';

export interface ExpandableTableRowProps<T> extends Pick<
  ExpandableTableProps<T>,
  'renderExpandedContent' | 'expandAriaLabel' | 'collapseAriaLabel'
> {
  row: Row<T>;
}

export const ExpandableTableRow = <T,>({
  row,
  renderExpandedContent,
  expandAriaLabel,
  collapseAriaLabel,
}: ExpandableTableRowProps<T>): React.ReactElement => {
  const isExpanded = row.getIsExpanded();
  const toggleExpanded = row.getToggleExpandedHandler();

  return (
    <>
      <TableRow
        onClick={toggleExpanded}
        className={cn(
          isExpanded && '[&>td:first-child]:rounded-bl-none [&>td:last-child]:rounded-br-none'
        )}
      >
        {row.getVisibleCells().map(cell => (
          <TableCell key={cell.id} className={cn('cursor-pointer', isExpanded && 'border-b-0')}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
        <TableCell className={cn('w-10', isExpanded && 'border-b-0')}>
          <button
            type="button"
            className="text-denim-500 hover:text-denim-800 inline-flex size-8 items-center justify-center rounded-sm"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? collapseAriaLabel : expandAriaLabel}
            onClick={event => {
              event.stopPropagation();
              toggleExpanded();
            }}
          >
            <ChevronDown
              className={cn('size-6 transition-transform duration-200', isExpanded && 'rotate-180')}
              aria-hidden
              strokeWidth={1.25}
            />
          </button>
        </TableCell>
      </TableRow>
      {isExpanded ? (
        <tr>
          <td
            colSpan={row.getVisibleCells().length + 1}
            className="-translate-y-2 rounded-b-lg border border-t-0 border-plum-200 bg-gray-100 p-0 align-top"
          >
            {renderExpandedContent(row.original)}
          </td>
        </tr>
      ) : null}
    </>
  );
};
